import { CheckoutEnum } from '@drycut/graphql';
import {
  Button,
  buttonVariants,
  Modal,
  Para,
  type RadioOption,
  RadioOptions,
  Stack,
} from '@drycut/ui-components';
import { type DateTime } from 'luxon';
import { useMemo, useState } from 'react';

import { useCheckoutStore } from '../../checkoutStore';
import { RulesAcceptance } from './RulesAcceptance';
import { ScheduleSelect } from './ScheduleSelect';

export function ScheduleStep(): React.ReactElement {
  const [showRules, setShowRules] = useState<boolean>(true);
  const [scheduleOpen, setScheduleOpen] = useState<boolean>(false);
  const selectedSchedule = useCheckoutStore((state) => state.selectedSchedule);
  const manual = useCheckoutStore((state) => state.manual);
  const scheduled = useCheckoutStore((state) => state.scheduled);
  const setSelectedSchedule = useCheckoutStore((state) => state.setSelectedSchedule);
  const toggleScheduled = useCheckoutStore((state) => state.toggleScheduled);
  const selectedStore = useCheckoutStore((state) => state.selectedStore);

  const { wrapper, text } = buttonVariants({
    fill: 'ghost',
    size: 'small',
  });

  type ScheduleOption = RadioOption & { id: 'instant' | 'scheduled' };

  const preorderDescription = useMemo(() => {
    if (selectedSchedule) {
      return (
        <Stack direction="col" gap={1} className="items-start">
          <Para size="sm">Deine Buching für</Para>
          <Stack direction="row" gap={1}>
            <Para as="span" weight="bold" size="sm">
              {selectedSchedule.setLocale('de-CH').toFormat("EEE 'ca. ' HH:mm")}
            </Para>
            <Para size="sm">(+/- 10 min)</Para>
          </Stack>

          <Button
            size="small"
            onClick={() => setScheduleOpen(true)}
            className={wrapper({
              className: 'p-1 hover:bg-primary-300/10 active:bg-primary-300/15',
            })}
          >
            <Para
              align="left"
              className={text({
                className:
                  'underline underline-offset-2 group-data-checked:text-primary-600 text-left',
              })}
            >
              Ändern
            </Para>
          </Button>
        </Stack>
      );
    } else {
      return (
        <Para size="sm" pretty>
          DRYCUT stellt Dich <u>später</u> in die Schlange
        </Para>
      );
    }
  }, [selectedSchedule, wrapper, text]);

  const preBookingTax = selectedStore?.preBookingTax ?? 0;

  const options: ScheduleOption[] = useMemo(() => {
    const availableOptions: ScheduleOption[] = [];

    // If the user didn't select a store we can leave it empty
    if (!selectedStore) {
      return availableOptions;
    }

    if (selectedStore.checkoutType !== CheckoutEnum.Scheduled) {
      availableOptions.push({
        id: 'instant',
        name: 'Sofort schneiden',
        description: (
          <Para size="sm" pretty>
            Sichere Dir den <u>nächsten</u> verfügbaren Cut
          </Para>
        ),
        disabled: false,
      });
    }

    // Do not show pre-booking option if the store only allows instant checkout or preBooking is
    // disabled (manual booking mode)
    if (selectedStore.checkoutType !== CheckoutEnum.Instant && !manual) {
      availableOptions.push({
        id: 'scheduled',
        name: preBookingTax > 0 ? `Später schneiden (+${preBookingTax} CHF)` : 'Später schneiden',
        description: preorderDescription,
        disabled: false,
      });
    }

    return availableOptions;
  }, [selectedStore, preorderDescription, preBookingTax, manual]);

  // Look the option up by id: depending on the store checkout type and the manual mode only one of
  // the two options may be available, so the position in the array is not reliable
  const selectedOption = useMemo(() => {
    return options.find((option) => option.id === (scheduled ? 'scheduled' : 'instant')) ?? null;
  }, [scheduled, options]);

  const handleScheduleClose = () => {
    if (!selectedSchedule) {
      toggleScheduled();
    }
    setScheduleOpen(false);
  };

  const acceptRules = () => {
    setShowRules(false);
  };

  // When the user selects a slot from the schedule select component
  const handleSlotSelect = (date: DateTime) => {
    setSelectedSchedule(date);
    setScheduleOpen(false);
  };

  // When the user changes the radio option (there are usually two: instant and scheduled)
  const handleRadioChange = (option: null | RadioOption) => {
    const scheduledOption = options.find((opt) => opt.id === 'scheduled');
    toggleScheduled();

    if (option && option.name === scheduledOption?.name) {
      setScheduleOpen(true);
    } else {
      setSelectedSchedule();
    }
  };

  return (
    <>
      <div className="relative">
        <RadioOptions options={options} selected={selectedOption} onChange={handleRadioChange} />
      </div>

      <Modal
        title="Wähle Dein Zeitfenster aus"
        open={scheduleOpen}
        closeButton
        onClose={handleScheduleClose}
        className="max-w-prose w-full sm:w-auto relative"
      >
        {showRules && <RulesAcceptance onAccept={acceptRules} onCancel={handleScheduleClose} />}

        <Para size="sm" pretty>
          Die Reservierung ist keine feste Buchung. Sie bedeutet, dass Du zur gewünschten Zeit in
          die Warteschlange kommst. Den genauen Startzeitpunkt für den Haarschnitt bekommst Du vorab
          per SMS. Er kann 10–15 Minuten später sein als die gewünschte Uhrzeit.
        </Para>

        <ScheduleSelect onSlotSelect={handleSlotSelect} />
      </Modal>
    </>
  );
}
